import React, { useEffect, useState } from "react";
import {
  Grid,
  IconButton,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import { getSupportPlanLogsService } from "../services/getSupportPlanLogs";
import moment from "moment";
import { FiberManualRecord, Publish } from "@mui/icons-material";
import TableLoader from "./shared/loaders/TableLoader";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { downloadExcel } from "../utils/exportExcel";

const SupportPlanLogs = ({ userId: userId1, nameFull }) => {
  const [orderBy, setOrderBy] = useState("logDate");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(new Date());
  const [tableLoader, setTableLoader] = useState(true);
  const user = localStorage.getItem("user") ?? null;
  const username = nameFull ? nameFull : JSON.parse(user).user?.nameFull;

  const WORKSHEET_COLUMNS = [
    {
      key: "name",
      header: `${username} Support Logs`,
      width: 30,
    },
    {
      key: "dateTime",
      header: "Date Time",
      width: 30,
    },
    {
      key: "sessions",
      header: "Sessions",
      width: 30,
    },
  ];

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (date && endDate && date > endDate) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (date && startDate && date < startDate) {
      setStartDate(null);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const isInDateRange = (logDate) => {
    const updatedStartDate = moment(startDate).format("YYYY-MM-DD");
    const updatedEndDate = moment(endDate).format("YYYY-MM-DD");
    const updatedLogDate = moment(logDate).utc().format("YYYY-MM-DD");

    if (startDate && endDate) {
      return moment(updatedLogDate).isBetween(
        updatedStartDate,
        updatedEndDate,
        null,
        "[]",
      );
    }
    return true;
  };

  const handleFilterDateChange = () => {
    const logsByDate = logs.filter((log) => isInDateRange(log.logDate));
    setFilteredLogs(logsByDate);
    setPage(0);
  };

  const stableSortedLogs = stableSort(
    filteredLogs.length > 0 ? filteredLogs : logs,
    getComparator(order, orderBy),
  );
  const sortedLogs = stableSortedLogs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleDownloadExcel = () => {
    const fileName = "SupportPlanLogs";

    // Prepare data for the utility function
    const processedData = stableSortedLogs?.map((row) => ({
      name: row.username,
      dateTime: moment.utc(row.logDate).format("DD MMM YYYY hh:mm a"),
      sessions: row.isCurrentSession ? "Active" : "Expire",
    })) || [];

    // Define columns for the utility function
    const columns = [
      { key: "name", header: "Name", width: 30 },
      { key: "dateTime", header: "Date Time", width: 30 },
      { key: "sessions", header: "Sessions", width: 30 },
    ];

    downloadExcel(fileName, columns, processedData);
  };

  useEffect(() => {
    if (user) {
      const userId = userId1 ? userId1 : JSON.parse(user).user?.usersId;
      getSupportPlanLogsService(userId)
        .then((logsResponse) => {
          setLogs(logsResponse);
          setTableLoader(false);
        })
        .catch(() => setTableLoader(false));
    } else {
      setTableLoader(false);
    }
  }, []);

  return (
    <>
      <Grid
        container
        direction={"row"}
        spacing={2}
        alignItems={"center"}
        sx={{ mt: 1, mb: 2 }}
      >
        <Grid item sx={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              className="mui-date-picker"
              label="Start Date"
              format="MM/dd/yyyy"
              value={startDate}
              onChange={handleStartDateChange}
              placeholder="MM/DD/YYYY"
              onError={console.log}
              id="date-picker-inline"
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item sx={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              className="mui-date-picker"
              label="End Date"
              format="MM/dd/yyyy"
              InputAdornmentProps={{ position: "start" }}
              value={endDate}
              onChange={handleEndDateChange}
              placeholder="MM/DD/YYYY"
              onError={console.log}
              id="date-picker-inline"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item sx={4}>
          <Button
            size={"small"}
            variant={"contained"}
            color={"primary"}
            onClick={handleFilterDateChange}
          >
            Filter
          </Button>
          <Button
            style={{ marginLeft: "1rem" }}
            size={"small"}
            variant={"contained"}
            color={"default"}
            onClick={() => {
              setFilteredLogs([]);
              setStartDate(null);
            }}
          >
            Reset
          </Button>
        </Grid>
        <Grid item>
          <Button
            size={"small"}
            type="button"
            variant="contained"
            color="default"
            disabled={sortedLogs?.length === 0}
            startIcon={<Publish />}
            onClick={handleDownloadExcel}
          >
            Export CSV
          </Button>
        </Grid>
      </Grid>
      <TableContainer>
        {tableLoader ? (
          <TableLoader />
        ) : (
          <Table
            style={{ minWidth: 650, maxHeight: "20rem" }}
            aria-label="logs table"
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "username"}
                    direction={order}
                    onClick={() => handleRequestSort("username")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    align={"center"}
                    active={orderBy === "logDate"}
                    direction={order}
                    onClick={() => handleRequestSort("logDate")}
                  >
                    Date Time
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "isCurrentSession"}
                    direction={order}
                    onClick={() => handleRequestSort("isCurrentSession")}
                  >
                    Sessions
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLogs?.length > 0 ? (
                sortedLogs.map((log, index) => (
                  <TableRow key={index}>
                    <TableCell component="th" scope="row">
                      {log?.username}
                    </TableCell>
                    <TableCell align="left">
                      {log.logDate
                        ? moment.utc(log.logDate).format("DD MMM YYYY hh:mm a")
                        : "---"}
                    </TableCell>
                    <TableCell align="left">
                      <FiberManualRecord
                        size={"small"}
                        style={{
                          color: log?.isCurrentSession ? "green" : "red",
                        }}
                      />
                      {log?.isCurrentSession ? " Active" : " Expire"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    No Logs
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredLogs.length > 0 ? filteredLogs.length : logs.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </>
  );
};

export default SupportPlanLogs;
